import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, CornerDownLeft, Loader2, X, ArrowRight } from 'lucide-react';
import { API } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagContext';
import { useNavigationItems } from '../context/NavigationContext';
import { REPORTS, isReportVisible } from '../config/reportCatalog';
import useReportSections from '../hooks/useReportSections';
import { useMoney } from '../context/MoneyContext';
import { DESTINATIONS } from '../config/destinationCatalog';
import { destinationPath } from '../utils/deepLink';
import { useOpenRecord } from '../hooks/useRecordFocus';
import { RECORD_TYPE_LABELS, routeTarget } from '../utils/recordRoutes';
import ScanButton from './native/ScanButton';
import { inAppPathFromScan, normalizeCode } from '../utils/nativeScan';

// ── Global search (⌘K) ────────────────────────────────────────────────────────
// The operator's side of "I have a code, where does it live". Any printed code —
// lot number, batch, traveler, line item, FG code, order number, invoice no.,
// buyer, agent — is typed once and answered from every module the tenant has on.
// The result opens the record's own screen and flashes the row, using exactly the
// mechanism the attention alerts use (`RECORD_ROUTES` → `useOpenRecord`), so
// there is one definition of where a kind of record is worked.

const MIN_QUERY = 2;
const DEBOUNCE_MS = 220;
// A code search answers "where is this record"; a name search answers "where is
// this screen". Both are things an operator types into the same box, so pages
// the tenant actually has switched on are matched locally — instantly, with no
// request — and shown above the record hits.
const MAX_PAGE_HITS = 6;
// Tabs and panels are the third thing an operator types: "stock pick suggestions"
// is a card on a page whose name shares not one word with it. Kept below the page
// hits and tighter than them — a page is a bigger, safer answer than a card on it.
const MAX_DESTINATION_HITS = 5;

// Rank: whole-name prefix beats word-start beats anywhere, so typing "cold"
// puts Cold Storage above a page that merely mentions it.
function scorePage(item, groupLabel, term) {
  const name = (item.name || '').toLowerCase();
  const group = (groupLabel || '').toLowerCase();
  const path = (item.path || '').toLowerCase();
  if (name.startsWith(term)) return 0;
  if (name.split(/[^a-z0-9]+/).some((w) => w.startsWith(term))) return 1;
  if (name.includes(term)) return 2;
  if (`${group} ${name}`.includes(term)) return 3;
  if (path.includes(term.replace(/\s+/g, '-'))) return 4;
  return -1;
}

const PAGE_HIT_LABELS = { report: 'Report', tab: 'Tab', panel: 'Panel', page: 'Page' };

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

export default function GlobalSearch() {
  const { isEnabled, features } = useFeatureFlags();
  const { moneyLevel } = useMoney();
  const { sectionsData } = useReportSections();
  const enabled = isEnabled('globalSearch');
  const navigate = useNavigate();
  const { items: navItems, groupLabels } = useNavigationItems();

  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const closeTimer = useRef(null);
  const openRecord = useOpenRecord();
  // Every keystroke starts a request; only the newest one may paint. Without this
  // a slow answer for "K4" lands after "K401" and overwrites the right list.
  const seq = useRef(0);

  const openPalette = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setClosing(false);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setClosing(true);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setClosing(false);
      setQuery('');
      setResults([]);
      setError('');
      setActive(0);
    }, 210);
  }, []);

  // A scanned barcode or QR (store app). A label carrying a link into this
  // workspace opens that page; a code that names exactly one record opens it
  // straight away — the whole point of scanning is not typing; anything else
  // lands in the palette with the code typed in, to pick from.
  const onScanned = useCallback(async (text) => {
    const path = inAppPathFromScan(text);
    if (path) {
      navigate(path);
      return;
    }
    try {
      const res = await axios.get(`${API}/search`, { params: { q: text } });
      const want = normalizeCode(text);
      const exact = (res.data?.results || []).filter((h) => [h.code, ...(h.keys || [])]
        .some((k) => k && normalizeCode(k) === want));
      if (exact.length === 1) {
        const hit = exact[0];
        const target = routeTarget(hit.type, hit.id, [hit.code, ...(hit.keys || [])]);
        if (target) {
          openRecord(target);
          return;
        }
      }
    } catch {
      /* fall through to the palette, which reports its own errors */
    }
    openPalette();
    setQuery(text);
  }, [navigate, openRecord, openPalette]);

  // ⌘K / Ctrl-K from anywhere, Esc to leave. Bound on the document so the palette
  // is reachable without the pointer ever leaving the keyboard.
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (open) close();
        else openPalette();
      } else if (e.key === 'Escape' && open) {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled, open, close, openPalette]);

  useEffect(() => () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_QUERY) {
      setResults([]);
      setLoading(false);
      setError('');
      return undefined;
    }
    const mine = ++seq.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search`, { params: { q: term } });
        if (mine !== seq.current) return;
        setResults(res.data?.results || []);
        setError('');
        setActive(0);
      } catch (err) {
        if (mine !== seq.current) return;
        setResults([]);
        setError(err?.response?.status === 403
          ? 'Search is switched off for this tenant.'
          : 'Search failed. Try again.');
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Pages the *sidebar* would show this user — already filtered by tenant flag,
  // page permissions and role — so search can never offer a screen that would
  // bounce the operator back with "not enabled for your organization".
  const pageHits = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < MIN_QUERY) return [];
    return navItems
      .map((item) => ({ item, rank: scorePage(item, groupLabels[item.group], term) }))
      .filter((r) => r.rank >= 0)
      .sort((a, b) => a.rank - b.rank || a.item.name.localeCompare(b.item.name))
      .slice(0, MAX_PAGE_HITS)
      .map(({ item }) => ({
        kind: 'page',
        type: 'page',
        id: item.path,
        title: item.name,
        subtitle: groupLabels[item.group] || 'Navigation',
        path: item.path,
      }));
  }, [query, navItems, groupLabels]);

  // Tabs and named panels, from the generated catalogue. A destination is only
  // offered when its page is in *this* user's sidebar, so search can never point
  // at a screen their tenant or role does not have — the same gate the page hits
  // use, for the same reason.
  const destinationHits = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < MIN_QUERY) return [];
    const navPaths = navItems.map((i) => i.path);
    // A page reached from a visible one (Masters → its sections, Cold Storage →
    // its masters) is reachable too; the route guards still have the last word.
    const visible = (path) => navPaths.some((p) => p === path || path.startsWith(`${p}/`));
    const tokens = term.split(/\s+/).filter(Boolean);
    return DESTINATIONS
      .filter((d) => visible(d.path) && (d.features || []).every((feature) => isEnabled(feature)))
      .map((d) => {
        const name = d.name.toLowerCase();
        // Every word typed has to appear somewhere in the destination, so "pick
        // suggestions" and "suggestions pick" both land, while a single shared
        // word does not drag in half the catalogue.
        const haystack = `${name} ${d.page.toLowerCase()}`;
        if (!tokens.every((t) => haystack.includes(t))) return { d, rank: -1 };
        if (name.startsWith(term)) return { d, rank: 0 };
        if (name.split(/[^a-z0-9]+/).some((w) => w.startsWith(tokens[0]))) return { d, rank: 1 };
        if (name.includes(term)) return { d, rank: 2 };
        return { d, rank: 3 };
      })
      .filter((x) => x.rank >= 0)
      .sort((a, b) => a.rank - b.rank
        || (a.d.kind === b.d.kind ? 0 : a.d.kind === 'tab' ? -1 : 1)
        || a.d.name.localeCompare(b.d.name))
      .slice(0, MAX_DESTINATION_HITS)
      .map(({ d }) => ({
        kind: 'page',
        type: d.kind === 'tab' ? 'tab' : 'panel',
        id: d.id,
        title: d.name,
        subtitle: `${d.page}${d.kind === 'tab' ? ' · tab' : ' · panel'}`,
        path: destinationPath(d),
        module: d.page,
      }));
  }, [query, navItems, isEnabled]);

  // Reports are features too, and each one is a deep link the operator would
  // otherwise reach by scrolling the catalogue. Only offered when the Reports
  // page itself is visible to this user, and only for reports whose flag is on.
  const reportHits = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < MIN_QUERY) return [];
    if (!navItems.some((i) => i.path === '/reports')) return [];
    return REPORTS
      // Same money rule as the Reports page. A catalogue entry hidden on one
      // surface and offered on the other is not hidden.
      .filter((r) => isReportVisible(r, { isEnabled, features, moneyLevel, sectionsData }))
      .map((r) => ({ r, rank: scorePage({ name: r.name, path: `/reports/${r.key}` }, 'Reports', term) }))
      .filter((x) => x.rank >= 0)
      .sort((a, b) => a.rank - b.rank || a.r.name.localeCompare(b.r.name))
      .slice(0, MAX_PAGE_HITS)
      .map(({ r }) => ({
        kind: 'page',
        type: 'report',
        id: `report:${r.key}`,
        title: r.name,
        subtitle: 'Reports',
        path: `/reports?report=${r.key}`,
      }));
  }, [query, navItems, isEnabled, features, moneyLevel, sectionsData]);

  // One ranked list behind the visual groups: pages, then reports, then records,
  // so ↑/↓/↵ walk exactly what is on screen.
  const hits = useMemo(
    () => [...pageHits, ...destinationHits, ...reportHits,
      ...results.map((hit) => ({ ...hit, kind: 'record' }))],
    [pageHits, destinationHits, reportHits, results],
  );

  // Results arrive flat and ranked; the grouping is presentational only, so the
  // keyboard still walks the ranked order rather than the visual order.
  const grouped = useMemo(() => {
    const out = [];
    hits.forEach((hit, index) => {
      const module = hit.kind !== 'page' ? hit.module
        : hit.type === 'report' ? 'Reports'
          : hit.type === 'tab' || hit.type === 'panel' ? 'Tabs & panels'
            : 'Pages';
      const last = out[out.length - 1];
      if (last && last.module === module) last.items.push({ hit, index });
      else out.push({ module, items: [{ hit, index }] });
    });
    return out;
  }, [hits]);

  const openHit = useCallback((hit) => {
    if (!hit) return;
    if (hit.kind === 'page') {
      close();
      navigate(hit.path);
      return;
    }
    if (hit.type === 'documentation') {
      close();
      navigate(`/docs/${hit.id}`);
      return;
    }
    const target = routeTarget(hit.type, hit.id, [hit.code, ...(hit.keys || [])]);
    close();
    if (target) openRecord(target);
  }, [close, openRecord, navigate]);

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(0, hits.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      openHit(hits[active]);
    }
  };

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, hits]);

  if (!enabled) return null;

  return (
    <>
      <button
        type="button"
        onClick={openPalette}
        data-testid="global-search-trigger"
        title="Search anything — a page, or a production day, lot, batch, traveler, finished good, order, invoice"
        className="ios-spotlight-trigger"
      >
        <Search className="ios-spotlight-trigger-icon" size={20} strokeWidth={2.15} />
        <span className="ios-spotlight-trigger-text">Search anything...</span>
        <kbd className="ios-spotlight-kbd">
          {isMac ? '⌘' : 'Ctrl '}K
        </kbd>
      </button>
      <ScanButton onScan={onScanned} />

      {(open || closing) && (
        <div
          className={`ios-spotlight-scrim ${closing ? 'is-closing' : 'is-open'}`}
          onMouseDown={close}
        >
          <div
            className={`ios-spotlight-panel ${closing ? 'is-closing' : 'is-open'}`}
            onMouseDown={(e) => e.stopPropagation()}
            data-testid="global-search-panel"
          >
            <div className="ios-spotlight-input-row">
              {loading ? <Loader2 size={22} className="animate-spin text-slate-400" />
                : <Search size={23} strokeWidth={2.1} className="text-slate-400" />}
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="Search anything..."
                className="ios-spotlight-input"
                data-testid="global-search-input"
              />
              <button type="button" onClick={close} className="ios-spotlight-close" aria-label="Close search">
                <X size={16} />
              </button>
            </div>

            <div ref={listRef} className="ios-spotlight-results">
              {query.trim().length < MIN_QUERY && (
                <p className="ios-spotlight-empty">
                  Type at least {MIN_QUERY} characters — a page name, or any code printed on a slip, a chip or an alert.
                </p>
              )}
              {error && hits.length === 0 && <p className="ios-spotlight-empty text-red-500">{error}</p>}
              {!error && query.trim().length >= MIN_QUERY && !loading && hits.length === 0 && (
                <p className="ios-spotlight-empty">
                  Nothing matches “{query.trim()}”.
                </p>
              )}
              {grouped.map((group) => (
                <div key={group.module}>
                  <p className="ios-spotlight-group">
                    {group.module}
                  </p>
                  {group.items.map(({ hit, index }) => (
                    <button
                      key={`${hit.type}:${hit.id}`}
                      type="button"
                      data-active={index === active}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => openHit(hit)}
                      className={`ios-spotlight-result ${index === active ? 'is-active' : ''}`}
                    >
                      <span className="ios-spotlight-result-type">
                        {hit.kind === 'page'
                          ? (PAGE_HIT_LABELS[hit.type] || 'Page')
                          : (RECORD_TYPE_LABELS[hit.type] || hit.type.replace(/_/g, ' '))}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-sm font-semibold text-slate-800 ${hit.kind === 'page' ? '' : 'font-mono'}`}>
                          {hit.title}
                        </span>
                        {hit.subtitle && (
                          <span className="block truncate text-xs text-slate-500">{hit.subtitle}</span>
                        )}
                      </span>
                      {index === active
                        ? <CornerDownLeft size={13} className="shrink-0 text-slate-400" />
                        : hit.kind === 'page' && <ArrowRight size={13} className="shrink-0 text-slate-300" />}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <div className="ios-spotlight-footer">
              <span>↑↓ move · ↵ open · Esc close</span>
              <span>{results.length > 0 ? `${results.length} match${results.length === 1 ? '' : 'es'}` : ''}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
